import { useState } from 'react'
import type { AssignableRole, RoleGrant } from '../../lib/roles'
import { InfoIcon } from '../icons'

/**
 * Store manager, on one line, with one chip per store (140).
 *
 * ── WHY IT IS NOT A RoleRow ─────────────────────────────────────────────────
 *
 * RoleRow's toggle means WHOLE COMPANY and its scope picker lists org units.
 * Neither applies here. There is no tenant-wide "store manager" — a Stock
 * Administrator is the person who runs every store — and a store is a place,
 * not an org unit: the stores one person runs need not share a branch (064).
 * So there is no Grant toggle, only "Add to a store", and each store is its
 * own removable chip, exactly as a scoped role's grants are.
 *
 * ── THE SERVER DECIDES ──────────────────────────────────────────────────────
 *
 * add_stock_room_manager / remove_stock_room_manager are SECURITY DEFINER and
 * app.require_store_manager_authority is the boundary: shop.admin, or
 * admin.roles behind 137's line. `lockedReason` says why before anybody
 * presses anything; the refusal is still the gate.
 */
export type StoreOption = { id: string; name: string; isActive: boolean }

export function StoreRow({
  role,
  held,
  stores,
  membershipActive,
  lockedReason = null,
  busy,
  onAdd,
  onRemove,
}: {
  role: AssignableRole
  /** Every store this person runs — scopeId is the store's id. */
  held: RoleGrant[]
  /** Every store in the tenant, open and closed. Only open ones are offered. */
  stores: StoreOption[]
  membershipActive: boolean
  lockedReason?: string | null
  /** The token currently in flight, or null. */
  busy: string | null
  onAdd: (storeId: string) => void
  onRemove: (storeId: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const [store, setStore] = useState('')

  const working = busy !== null
  const locked = lockedReason !== null
  const heldIds = new Set(held.map((g) => g.scopeId))
  const offered = stores.filter((s) => s.isActive && !heldIds.has(s.id))

  return (
    <li className={`rolerow${held.length > 0 ? ' rolerow--held' : ''}`}>
      <div className="rolerow__line">
        <span className="rolerow__name">
          {role.name}
          {held.length > 1 && <span className="admin__muted"> · {held.length} stores</span>}
        </span>

        <button
          type="button"
          className="rolerow__info"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          title={role.what}
        >
          <InfoIcon size={15} />
          <span className="visually-hidden">What a {role.name} can do</span>
        </button>
      </div>

      {open && (
        <p className="rolerow__what">
          {role.what}
          {role.caution && <em className="rolerow__caution">{role.caution}</em>}
        </p>
      )}

      {locked && lockedReason && <p className="rolerow__locked">{lockedReason}</p>}

      {held.length > 0 && (
        <ul className="rolerow__scopes">
          {held.map((g) => (
            <li key={g.scopeId ?? g.scopeName}>
              <span className="chip chip--scoped">
                {g.scopeName ?? 'A store'}
                {g.scopeActive === false && <span className="chip__scope">closed</span>}
              </span>
              <button
                type="button"
                className="btn btn--quiet btn--xs"
                disabled={working || locked || !g.scopeId}
                onClick={() => g.scopeId && onRemove(g.scopeId)}
              >
                {busy === `remove:${g.scopeId}` ? '…' : 'Remove'}
              </button>
            </li>
          ))}
        </ul>
      )}

      {membershipActive && !locked && (
        <div className="rolerow__scopeadd">
          {!adding ? (
            <button
              type="button"
              className="btn btn--quiet btn--xs"
              disabled={working || offered.length === 0}
              onClick={() => setAdding(true)}
            >
              {offered.length === 0 && stores.length > 0 ? 'Runs every open store' : 'Add to a store'}
            </button>
          ) : (
            <>
              <label className="admin__field">
                <span className="visually-hidden">Store for {role.name}</span>
                <select value={store} onChange={(e) => setStore(e.target.value)} disabled={working}>
                  <option value="">Choose…</option>
                  {offered.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="btn btn--primary btn--xs"
                disabled={working || store === ''}
                onClick={() => {
                  onAdd(store)
                  setStore('')
                  setAdding(false)
                }}
              >
                Add
              </button>
              <button
                type="button"
                className="btn btn--quiet btn--xs"
                disabled={working}
                onClick={() => {
                  setAdding(false)
                  setStore('')
                }}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      )}
    </li>
  )
}
